import { AddressForm } from "@/components/address/AddressForm";
import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import {
  datePickerFormItemProps,
  datePickerStandardProps,
  selectStandardProps,
  selectTagsStandardProps,
} from "@/utils/componentUtils";
import { licensePlateNormalizeFunction, resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { useFilterSpecificParams } from "@/utils/hooksUtils";
import { dateToIsoDateString } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import {
  App as AntApp,
  Button,
  Card,
  Cascader,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
} from "antd";
import { DefaultOptionType } from "antd/es/select";
import { FormInstance, Rule } from "antd/lib/form";
import dayjs from "dayjs";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import t from "../../../../app/i18n";
import { useGenerateContractTermination, useSearchContract } from "../../../../common/api/queries";
import { rowGutter } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { ClientTypeTag } from "../../../client/components/tags/ClientTypeTag";
import { ClientType, clientTypeTMap } from "../../../client/enums";
import { useClientSearch } from "../../../client/hooks";
import { Client, ClientRepresentative, LegalClient } from "../../../client/types";
import { formatClientName } from "../../../client/utils";
import { isOtherVehicleInsurance, isVehicleInsurance } from "../../../contract/utils";
import AcademicDegreeAfterSelect from "../../../enumerations/components/form/AcademicDegreeAfterSelect";
import AcademicDegreeSelect from "../../../enumerations/components/form/AcademicDegreeSelect";
import CityAutoComplete from "../../../enumerations/components/form/CityAutoComplete";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import { InstitutionType } from "../../../institution/enums";
import { ContractTerminationCategory, ContractTerminationReason } from "../../enums";
import { ContractFormClient, ContractFormSearchContract, GenerateContractTermination } from "../../types";
import { CONTRACT_TERMINATION_REASONS_MAP, INSURANCE_TYPE_TO_TERMINATION_CATEGORY_MAP } from "../../utils";

type ClientKind = "policyHolder" | "representative";

interface TerminationClientSectionProps {
  form: FormInstance<GenerateContractTermination>;
  kind: ClientKind;
  sectionLabel: string;
  clientType?: ClientType;
  representatives?: ClientRepresentative[];
  currentFocusedKind?: ClientKind;
  searchInProgress: boolean;
  onFocus: (kind: ClientKind) => void;
  onSearch: (pinOrCrn: string, kind: ClientKind) => void;
}

const reasonCorrectlySelectedRule: Rule = {
  validator: (_, value) =>
    value && value.length !== 2
      ? Promise.reject(t("contractTermination.validations.reasonCorrectlySelected"))
      : Promise.resolve(),
};

const colSpan = 4;

const TerminationClientSection = ({
  form,
  kind,
  sectionLabel,
  clientType,
  representatives,
  currentFocusedKind,
  searchInProgress,
  onFocus,
  onSearch,
}: TerminationClientSectionProps) => {
  const { t } = useTranslation();

  const type = Form.useWatch([kind, "type"], form) as ClientType | undefined;

  const handleRepresentativeChange = (id: UUID) => {
    form.setFieldsValue({ representative: representatives?.find((r) => r.id === id)?.representative });
  };

  return (
    <>
      <Divider titlePlacement="start">{sectionLabel}</Divider>

      {representatives && representatives?.length > 1 && (
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              label={
                <LabelWithTooltip
                  label={t("contractTermination.attrs.representative._label")}
                  tooltip={t("contractTermination.helpers.representativeDesc")}
                />
              }
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={representatives.map((r) => ({
                  value: r.id,
                  label: `${formatClientName(r.representative)} | ${t("client.enums.representativeFunction." + r.function)}`,
                }))}
                onChange={handleRepresentativeChange}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={rowGutter}>
        {clientType ? (
          <HiddenInput name={[kind, "type"]} initialValue={clientType} />
        ) : (
          <Col span={colSpan}>
            <Form.Item name={[kind, "type"]} label={t("client.enums.type._label")} rules={[validations.notNull]}>
              <Select
                {...selectTagsStandardProps(clientTypeTMap)}
                options={Object.values(ClientType).map((type) => ({
                  value: type,
                  label: <ClientTypeTag type={ClientType[type]} />,
                }))}
              />
            </Form.Item>
          </Col>
        )}

        {type === ClientType.NATURAL && (
          <>
            <Col span={colSpan}>
              <Form.Item
                name={[kind, "personalIdentificationNumber"]}
                label={t(`contractTermination.attrs.${kind}.personalIdentificationNumber`)}
                rules={[validations.pin]}
              >
                <Input.Search
                  loading={kind === currentFocusedKind && searchInProgress}
                  onFocus={() => onFocus(kind)}
                  onSearch={(value) => onSearch(value, kind)}
                  onChange={(e) => onSearch(e.target.value, kind)}
                />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <AcademicDegreeSelect
                formItemProps={{
                  name: [kind, "academicDegree"],
                  label: t(`contractTermination.attrs.${kind}.academicDegree`),
                }}
                selectProps={{ allowClear: true }}
              />
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={[kind, "firstName"]}
                label={t(`contractTermination.attrs.${kind}.firstName`)}
                rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.wordRegex)]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={[kind, "lastName"]}
                label={t(`contractTermination.attrs.${kind}.lastName`)}
                rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.wordRegex)]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <AcademicDegreeAfterSelect
                formItemProps={{
                  name: [kind, "academicDegreeAfter"],
                  label: t(`contractTermination.attrs.${kind}.academicDegreeAfter`),
                }}
                selectProps={{ allowClear: true }}
              />
            </Col>
          </>
        )}

        {(type === ClientType.SELF_EMPLOYED || type === ClientType.LEGAL) && (
          <>
            <Col span={colSpan}>
              <Form.Item
                name={[kind, "companyRegistrationNumber"]}
                label={t(`contractTermination.attrs.${kind}.companyRegistrationNumber`)}
                rules={[validations.notBlank, validations.crn]}
              >
                <Input.Search
                  loading={kind === currentFocusedKind && searchInProgress}
                  onFocus={() => onFocus(kind)}
                  onSearch={(value) => onSearch(value, kind)}
                  onChange={(e) => onSearch(e.target.value, kind)}
                />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={[kind, "companyName"]}
                label={t(`contractTermination.attrs.${kind}.companyName`)}
                rules={[validations.notBlank, validations.size(1, 255)]}
              >
                <Input />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      <AddressForm
        formItemsNamePath={[kind, "address"]}
        label={t(`contractTermination.attrs.${kind}.address`)}
        required
      />
    </>
  );
};

export const ContractTerminationForm = () => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm<GenerateContractTermination>();
  const policyHolderType = Form.useWatch(["policyHolder", "type"], form);

  const searchParams = useFilterSpecificParams<{ contractNumber: string }>(["contractNumber"]);
  const [searchKeyword, setSearchKeyword] = useState(searchParams.contractNumber);

  const { data, isLoading, refetch, isSuccess } = useSearchContract({ keyword: searchKeyword });
  const generateContractTermination = useGenerateContractTermination();
  const clientSearch = useClientSearch();

  const [focusedClientKind, setFocusedClientKind] = useState<ClientKind>();
  const [representatives, setRepresentatives] = useState<ClientRepresentative[]>([]);
  const [reasonSelectOpen, setReasonSelectOpen] = useState(false);

  const setClientToForm = (kind: ClientKind, client?: Partial<Client>) => {
    if (!client && kind === "representative") {
      form.setFieldsValue({ representative: { type: ClientType.NATURAL } as ContractFormClient });
    } else {
      form.setFieldsValue({ [kind]: client });
    }
  };

  const setRepresentativesOfClientToForm = (client: Partial<Client>) => {
    setClientToForm("representative");
    setRepresentatives([]);

    if (client.type === ClientType.LEGAL && (client as LegalClient).representatives?.length) {
      const representatives = (client as LegalClient).representatives;
      if (representatives?.length === 1) {
        setClientToForm("representative", representatives[0]?.representative);
      } else {
        setRepresentatives(representatives ?? []);
      }
    }
  };

  const setFormData = useCallback(
    (contract: ContractFormSearchContract) => {
      form.setFieldsValue({
        contractNumber: contract.identifier,
        institutionId: contract.institution.id,
        date: dateToIsoDateString(dayjs()),
        text: undefined,
        reason: contract.insurances[0]?.type
          ? [INSURANCE_TYPE_TO_TERMINATION_CATEGORY_MAP.get(contract.insurances[0].type) as ContractTerminationCategory]
          : [],
        city: undefined,
      });

      setClientToForm("policyHolder", contract.policyHolder);
      setRepresentativesOfClientToForm(contract.policyHolder);
      setReasonSelectOpen(true);

      messageApi.open({
        key: "loadContractInfo",
        type: "success",
        content: t("contractTermination.helpers.loadContractInfo"),
      });
    },
    [form],
  );

  useEffect(() => {
    return () => {
      clientSearch.onResultDelete();
    };
  }, []);

  useEffect(() => {
    if (searchParams.contractNumber) {
      form.setFieldsValue({ contractNumber: searchParams.contractNumber });
    }
  }, [searchParams.contractNumber]);

  useEffect(() => {
    const contract = data?.contract;

    if (contract) {
      setFormData(contract);
    }

    if (isSuccess && !data.contract) {
      messageApi.open({
        key: "loadContractInfo",
        type: "warning",
        content: t("contractTermination.helpers.noContractFound"),
      });

      form.setFieldsValue({
        text: undefined,
        date: undefined,
        city: undefined,
        policyHolder: undefined,
        representative: undefined,
        institutionId: undefined,
        reason: undefined,
      });
    }
  }, [data, setFormData, isSuccess]);

  useEffect(() => {
    if (clientSearch.result?.data && focusedClientKind) {
      const { keyword, clientType, data } = clientSearch.result;
      if (
        (clientType === ClientType.NATURAL &&
          keyword === form.getFieldValue([focusedClientKind, "personalIdentificationNumber"])) ||
        (clientType !== ClientType.NATURAL &&
          keyword === form.getFieldValue([focusedClientKind, "companyRegistrationNumber"]))
      ) {
        setClientToForm(focusedClientKind, data);

        if (focusedClientKind === "policyHolder") {
          setRepresentativesOfClientToForm(data);
        }
        setFocusedClientKind(undefined);
      }
    }
  }, [clientSearch.result]);

  const handleContractNumberSearchOrChangeDebounced = useMemo(() => debounce(setSearchKeyword, 500), []);

  const handleContractNumberSearchOrChange = useCallback(
    (value: string) => {
      handleContractNumberSearchOrChangeDebounced(value);
    },
    [handleContractNumberSearchOrChangeDebounced],
  );

  const handleClientSearch = (pinOrCrn: string, kind: ClientKind) => {
    if (pinOrCrn) {
      const type = form.getFieldValue([kind, "type"]) as ClientType;
      form
        .validateFields([
          [kind, type === ClientType.NATURAL ? "personalIdentificationNumber" : "companyRegistrationNumber"],
        ])
        .then(() => clientSearch.onSearch({ keyword: pinOrCrn, clientType: type }))
        .catch((errorInfo) => {
          if (focusedClientKind === "policyHolder" && representatives?.length > 0) {
            setRepresentatives([]);
          }
          resolveFormValidationError(errorInfo);
        });
    }
  };

  const handleReasonChange = (reason: ContractTerminationReason) => {
    if (reason) {
      let text = t(`contractTermination.helpers.terminationText.${reason}`);

      if (CONTRACT_TERMINATION_REASONS_MAP.get(ContractTerminationCategory.VEHICLE)?.includes(reason)) {
        const insurances = data?.contract?.insurances;
        const insurance = insurances?.[0];

        if (insurances?.length === 1 && insurance && isVehicleInsurance(insurance)) {
          text +=
            `\n\n${t("registerVehicleData.attrs.licensePlate")}: ${licensePlateNormalizeFunction(insurance.licensePlate)}\n` +
            `${t("registerVehicleData.attrs.vin")}: ${insurance.vehicle.vin}\n` +
            `${t("registerVehicleData.attrs.brandId")}: ${insurance.vehicle.model.brand.name}\n` +
            `${t("registerVehicleData.attrs.modelId")}: ${insurance.vehicle.model.name}`;
        } else if (insurances?.length === 1 && insurance && isOtherVehicleInsurance(insurance)) {
          text +=
            `\n\n${t("registerVehicleData.attrs.licensePlate")}: ${licensePlateNormalizeFunction(insurance.licensePlate)}\n` +
            `${t("registerVehicleData.attrs.vin")}: ${insurance.vin}`;
        }
      }

      text += `\n\n${t("contractTermination.helpers.terminationTextSuffix")}: `;

      form.setFieldsValue({ text });
    }
  };

  const handleContractSearch = async (value: string) => {
    setSearchKeyword(value);

    const { data } = await refetch();

    if (data?.contract) {
      setFormData(data?.contract);
    }
  };

  const handleFormSubmit = () => {
    form.validateFields().then((values) =>
      generateContractTermination.mutate(
        {
          ...values,
          reason: undefined,
          policyHolder: { ...values.policyHolder, personalIdentificationNumber: undefined },
          representative: values.representative
            ? { ...values.representative, personalIdentificationNumber: undefined }
            : undefined,
        },
        {
          onError: (error) => {
            setErrorsToForm(form, "contractTermination.attr", error.violations);
          },
        },
      ),
    );
  };

  const handleFormReset = () => {
    modal.confirm({
      title: t("contractTermination.helpers.resetFormConfirm"),
      okText: t("common.yes"),
      cancelText: t("common.back"),
      onOk: () => {
        form.resetFields();
        clientSearch.onResultDelete();
      },
    });
  };

  return (
    <Card className="card-box">
      {contextHolder}

      <Form form={form} layout="vertical" name="contractTerminationForm">
        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item
              name="contractNumber"
              label={t("contractTermination.attrs.contractNumber")}
              rules={[validations.notNull, validations.size(1, 64)]}
            >
              <Input.Search
                loading={isLoading}
                onSearch={handleContractSearch}
                onChange={(e) => handleContractNumberSearchOrChange(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        <TerminationClientSection
          form={form}
          kind="policyHolder"
          sectionLabel={t("contractTermination.sections.clientData")}
          currentFocusedKind={focusedClientKind}
          searchInProgress={clientSearch.inProgress}
          onFocus={setFocusedClientKind}
          onSearch={handleClientSearch}
        />

        {policyHolderType === ClientType.LEGAL ? (
          <TerminationClientSection
            form={form}
            kind="representative"
            sectionLabel={t("contractTermination.sections.representativeData")}
            clientType={ClientType.NATURAL}
            representatives={representatives}
            currentFocusedKind={focusedClientKind}
            searchInProgress={clientSearch.inProgress}
            onFocus={setFocusedClientKind}
            onSearch={handleClientSearch}
          />
        ) : undefined}

        <Divider titlePlacement="start">{t("contractTermination.sections.generalData")}</Divider>

        <Row gutter={rowGutter}>
          <Col span={8}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionId",
                label: t("contractTermination.attrs.institutionId"),
                rules: [validations.notNull],
              }}
              optionsProps={{ filterType: InstitutionType.INSURANCE_COMPANY }}
            />
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item
              name="reason"
              label={
                <LabelWithTooltip
                  label={t("contractTermination.enums.terminationReason._label")}
                  tooltip={t("contractTermination.helpers.reasonDesc")}
                />
              }
              rules={[reasonCorrectlySelectedRule]}
            >
              <Cascader<DefaultOptionType>
                allowClear
                expandTrigger="hover"
                open={reasonSelectOpen}
                options={Object.values(ContractTerminationCategory).map<DefaultOptionType>((category) => ({
                  label: t("contractTermination.enums.terminationCategory." + category),
                  value: category,
                  children: CONTRACT_TERMINATION_REASONS_MAP.get(category)
                    ?.sort((a, b) => {
                      const aLabel = t("contractTermination.enums.terminationReason." + a);
                      const bLabel = t("contractTermination.enums.terminationReason." + b);

                      return aLabel.localeCompare(bLabel);
                    })
                    .map<DefaultOptionType>((reason) => ({
                      label: t("contractTermination.enums.terminationReason." + reason),
                      value: reason,
                    })),
                }))}
                onChange={(value: (string | number | null)[]) =>
                  handleReasonChange(value?.[1] as ContractTerminationReason)
                }
                onOpenChange={setReasonSelectOpen}
              />
            </Form.Item>
          </Col>

          <Col span={16}>
            <Form.Item
              name="text"
              label={t("contractTermination.attrs.text")}
              rules={[validations.notBlank, validations.size(1, 4096)]}
            >
              <Input.TextArea rows={10} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item
              name="date"
              label={t("contractTermination.attrs.date")}
              rules={[validations.notNull]}
              initialValue={dayjs()}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerStandardProps} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <CityAutoComplete
              formItemProps={{
                name: "city",
                label: t("contractTermination.attrs.city"),
                rules: [validations.notBlank, validations.size(1, 64), validations.pattern(regexPatterns.wordRegex)],
              }}
            />
          </Col>
        </Row>
      </Form>

      <Space className="margin-top-medium">
        <Button type="primary" icon={<AntIcon type="download" />} onClick={handleFormSubmit}>
          {t("common.download")}
        </Button>

        <Button icon={<AntIcon type="reload" />} onClick={handleFormReset}>
          {t("contractTermination.actions.resetForm")}
        </Button>
      </Space>
    </Card>
  );
};
